import { supabase } from '@/lib/supabase-singleton';

/**
 * User preferences interface
 * Matches the columns in the public.user_preferences table
 */
export interface UserPreferences {
  sound_enabled: boolean;
  sound_volume: number;
  notification_enabled: boolean;
  theme: string;
  language?: string;
  dfn3_strength?: number;
}

/**
 * Database row shape (includes user_id and timestamps)
 */
interface UserPreferencesRow extends UserPreferences {
  id: string;
  user_id: string;
  created_at: string;
  updated_at: string;
}

/**
 * Default preferences for new users
 * These values are used when no database row exists
 */
const DEFAULT_PREFERENCES: UserPreferences = {
  sound_enabled: true,
  sound_volume: 0.5,
  notification_enabled: true,
  theme: 'dark',
  language: 'en',
  dfn3_strength: 70,
};

class UserPreferencesService {
  private preferences: UserPreferences | null = null;
  private listeners: Set<(prefs: UserPreferences) => void> = new Set();
  private userId: string | null = null;

  /**
   * Load user preferences from database or localStorage
   *
   * This method is robust to missing rows:
   * - If no row exists, returns defaults (does not throw)
   * - If database is unreachable, falls back to localStorage
   */
  async loadPreferences(): Promise<UserPreferences> {
    try {
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        // No authenticated user, use localStorage fallback
        return this.loadFromLocalStorage();
      }

      this.userId = user.id;

      // Use maybeSingle() instead of single() to avoid 406 errors
      // maybeSingle() returns null when no row exists, not an error
      const { data, error } = await supabase
        .from('user_preferences')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) {
        // Real database error (not just "no rows")
        console.error('[Preferences] Database error:', error.message);
        return this.loadFromLocalStorage();
      }

      if (data) {
        // Row exists - extract preferences
        this.preferences = this.extractPreferencesFromRow(data as UserPreferencesRow);
        this.saveToLocalStorage(this.preferences);
        return this.preferences;
      }

      // No row exists for this user - use defaults
      // The database trigger should auto-create rows, but handle the edge case
      this.preferences = { ...DEFAULT_PREFERENCES };
      this.saveToLocalStorage(this.preferences);

      // Optionally create the row in background (fire-and-forget)
      this.ensureRowExists(user.id).catch(() => {
        // Silent fail - the trigger should handle this
      });

      return this.preferences;

    } catch (error) {
      console.error('[Preferences] Failed to load from database:', error);
      return this.loadFromLocalStorage();
    }
  }

  /**
   * Extract UserPreferences from a database row
   * Applies defaults for any missing fields
   */
  private extractPreferencesFromRow(row: Partial<UserPreferencesRow>): UserPreferences {
    return {
      sound_enabled: row.sound_enabled ?? DEFAULT_PREFERENCES.sound_enabled,
      sound_volume: typeof row.sound_volume === 'number'
        ? row.sound_volume
        : DEFAULT_PREFERENCES.sound_volume,
      notification_enabled: row.notification_enabled ?? DEFAULT_PREFERENCES.notification_enabled,
      theme: row.theme ?? DEFAULT_PREFERENCES.theme,
      language: row.language ?? DEFAULT_PREFERENCES.language,
      dfn3_strength: typeof row.dfn3_strength === 'number'
        ? row.dfn3_strength
        : DEFAULT_PREFERENCES.dfn3_strength,
    };
  }

  /**
   * Ensure a preferences row exists for the given user
   * Uses upsert with onConflict to be idempotent
   */
  private async ensureRowExists(userId: string): Promise<void> {
    const { error } = await supabase
      .from('user_preferences')
      .upsert(
        {
          user_id: userId,
          ...DEFAULT_PREFERENCES,
          updated_at: new Date().toISOString(),
        },
        { onConflict: 'user_id' }
      );

    if (error) {
      console.error('[Preferences] Failed to ensure row exists:', error.message);
    }
  }

  /**
   * Update a specific preference
   *
   * Updates local state immediately, syncs to database in background
   */
  async updatePreference<K extends keyof UserPreferences>(
    key: K,
    value: UserPreferences[K]
  ): Promise<void> {
    if (!this.preferences) {
      this.preferences = await this.loadPreferences();
    }

    // Update local state
    this.preferences[key] = value;

    // Save to localStorage immediately (for offline resilience)
    this.saveToLocalStorage(this.preferences);

    // Notify listeners
    this.notifyListeners();

    // Sync with database (async, don't block)
    this.syncToDatabase().catch((error) => {
      console.error('[Preferences] Sync failed:', error);
    });
  }

  /**
   * Update multiple preferences at once
   */
  async updatePreferences(updates: Partial<UserPreferences>): Promise<void> {
    if (!this.preferences) {
      this.preferences = await this.loadPreferences();
    }

    // Merge updates
    this.preferences = { ...this.preferences, ...updates };

    // Save to localStorage immediately
    this.saveToLocalStorage(this.preferences);

    // Notify listeners
    this.notifyListeners();

    // Sync with database
    this.syncToDatabase().catch((error) => {
      console.error('[Preferences] Sync failed:', error);
    });
  }

  /**
   * Sync preferences to database using upsert with proper conflict handling
   *
   * KEY FIX: Uses onConflict: 'user_id' so repeated saves update the same row
   * instead of trying to insert a duplicate
   */
  private async syncToDatabase(): Promise<void> {
    if (!this.preferences) return;

    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      // No authenticated user, skip database sync
      return;
    }

    const { error } = await supabase
      .from('user_preferences')
      .upsert(
        {
          user_id: user.id,
          sound_enabled: this.preferences.sound_enabled,
          sound_volume: this.preferences.sound_volume,
          notification_enabled: this.preferences.notification_enabled,
          theme: this.preferences.theme,
          language: this.preferences.language,
          dfn3_strength: this.preferences.dfn3_strength,
          updated_at: new Date().toISOString(),
        },
        { onConflict: 'user_id' }
      );

    if (error) {
      console.error('[Preferences] Failed to sync to database:', error.message);
      throw error;
    }
  }

  /**
   * Save to localStorage for offline access
   */
  private saveToLocalStorage(prefs: UserPreferences): void {
    if (typeof window === 'undefined') return;

    try {
      localStorage.setItem('gjyl-user-preferences', JSON.stringify(prefs));
    } catch (error) {
      console.error('[Preferences] Failed to save to localStorage:', error);
    }
  }

  /**
   * Load from localStorage (fallback when database unavailable)
   */
  private loadFromLocalStorage(): UserPreferences {
    if (typeof window === 'undefined') {
      return { ...DEFAULT_PREFERENCES };
    }

    try {
      const stored = localStorage.getItem('gjyl-user-preferences');
      if (stored) {
        const parsed = JSON.parse(stored);
        // Merge with defaults to ensure all fields exist
        this.preferences = { ...DEFAULT_PREFERENCES, ...parsed };
        return this.preferences;
      }
    } catch (error) {
      console.error('[Preferences] Failed to load from localStorage:', error);
    }

    this.preferences = { ...DEFAULT_PREFERENCES };
    return this.preferences;
  }

  /**
   * Get default preferences
   */
  getDefaultPreferences(): UserPreferences {
    return { ...DEFAULT_PREFERENCES };
  }

  /**
   * Subscribe to preference changes
   * Returns an unsubscribe function
   */
  subscribe(listener: (prefs: UserPreferences) => void): () => void {
    this.listeners.add(listener);

    // Return unsubscribe function
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Notify all listeners of changes
   */
  private notifyListeners(): void {
    if (!this.preferences) return;

    const prefs = this.preferences;
    this.listeners.forEach(listener => {
      try {
        listener(prefs);
      } catch (error) {
        console.error('[Preferences] Listener error:', error);
      }
    });
  }

  /**
   * Get current preferences (cached)
   * Returns null if not yet loaded
   */
  getPreferences(): UserPreferences | null {
    return this.preferences;
  }

  /**
   * Force reload preferences from database
   */
  async reloadPreferences(): Promise<UserPreferences> {
    this.preferences = null;
    return this.loadPreferences();
  }

  /**
   * Clear cached preferences (useful on logout)
   */
  clearCache(): void {
    this.preferences = null;
    this.userId = null;
    if (typeof window !== 'undefined') {
      localStorage.removeItem('gjyl-user-preferences');
    }
  }
}

// Export singleton instance
export const userPreferencesService = new UserPreferencesService();

// Export defaults for external use
export { DEFAULT_PREFERENCES };
